import { SubdirectoryArrowRight } from '@mui/icons-material';
import { Checkbox, FormControlLabel, TextField } from '@mui/material';
import { useDispatch } from 'react-redux';
import type { FileObj } from 'src/app/common/components/FileUploader';
import FileUploader from 'src/app/common/components/FileUploader';
import { PruDateTimePicker } from 'src/app/common/components/PruDatePicker';
import { createBlob, getBlob } from 'src/app/common/network';
import { ETypeOfView } from 'src/app/common/types/view-types';
import { fileUpload, toAbsoluteUrl } from 'src/app/common/utils';
import type { SpecialDayItem } from 'src/app/modules/Event/types/special-day-type';
import { makeStyles } from 'tss-react/mui';
import Form from '../../../../../../../common/components/Form';

const useStyles = makeStyles()(() => ({
  form: { width: '100%', display: 'flex', flexDirection: 'column', gap: 16 },
  sectionTitle: { fontWeight: 700 },
  fieldRow: { display: 'flex', alignItems: 'center', gap: 12, minHeight: 36 },
  fieldColumn: { display: 'flex', flexDirection: 'column', gap: 12 },
  eventName: { width: 620 },
  dateRange: { display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 24 },
  datePicker: { width: 240 },
  dateSeparator: { color: '#6b7280', marginLeft: 16, fontWeight: 700 },
  reminderDays: { width: 80 },
  notificationSection: {
    border: '1px solid #d7d7d7',
    padding: '12px 12px 8px',
    borderRadius: 4,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  notificationTitle: { fontWeight: 700 },
  fullWidth: { flex: 1 },
  receiverRow: { display: 'flex', alignItems: 'center', gap: 18 },
  receiverLabel: { flex: '0 0 110px', color: '#4b5563' },
  receiverOptions: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  checkbox: { margin: 0, '& .MuiFormControlLabel-label': {} },
  receiverType: { minWidth: 160 },
  uploadRow: { display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 20 },
  templateLink: { paddingLeft: 55, paddingTop: 4 },
}));

const receiverTypeOptions = ['Channel', 'Roles', ETypeOfView.GroupOfUsers] as const;
const channelOptions = [
  { label: 'AD', value: 'AD' },
  { label: 'RED+', value: 'RED' },
] as const;
const roleOptions = [
  { value: 'AG', label: 'AG' },
  { value: 'MOA', label: 'MOA' },
  { value: 'GAD', label: 'GAD' },
] as const;
const spreadsheetFileTypes = [
  'csv',
  'xlsx',
  'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];
const receiverValidationMessage = 'Please select at least one Channel and one Role, or a Group of users.';

type SpecialDayFormValues = Partial<
  Omit<
    Pick<
      SpecialDayItem,
      | 'name'
      | 'startDate'
      | 'endDate'
      | 'reminder'
      | 'daysBeforeEvent'
      | 'reminderNotification'
      | 'eventNotification'
      | 'channels'
      | 'roles'
      | 'status'
      | 'groupOfUsers'
    >,
    'startDate' | 'endDate'
  >
> & {
  startDate?: Date;
  endDate?: Date;
  receiverTypes?: string[];
  userFile?: FileObj | null;
};

export type SpecialDayFormInstance = ReturnType<typeof Form.useForm<SpecialDayFormValues>>[0];

export type { SpecialDayFormValues };

type SpecialDayFormProps = {
  form?: SpecialDayFormInstance;
  initialValues?: SpecialDayFormValues;
  onFinish?: (values: SpecialDayFormValues) => void;
};

const requiredRule = (message: string) => ({
  required: true,
  message,
});

const dateRule = (message: string, validator?: (_: unknown, value: Date | null) => Promise<void>) => [
  requiredRule(message),
  ...(validator ? [{ validator }] : []),
];

const notificationTitleRules = (emptyMessage = 'Title is required') => [
  requiredRule(emptyMessage),
  { max: 255, message: 'Title cannot exceed 255 characters' },
  {
    validator: (_: unknown, value: string) => {
      if (value && !/^[\p{L}\p{N}\s]+$/u.test(value)) {
        return Promise.reject(new Error('Title cannot include special character'));
      }
      return Promise.resolve();
    },
  },
];

const notificationMessageRules = [
  requiredRule('Please enter a notification message'),
  { max: 500, message: 'Message cannot exceed 500 characters' },
];

export const SpecialDayForm = ({ form: externalForm, initialValues, onFinish }: SpecialDayFormProps) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const [internalForm] = Form.useForm<SpecialDayFormValues>();
  const form = externalForm || internalForm;

  const uploadGroupFile = async ({ file, fileName, fileType }: { file: File; fileName: string; fileType: string }) => {
    const createBlobResponse = await createBlob({ mimeType: fileType, accessLevel: 'anonymous' }, dispatch);
    await fileUpload(createBlobResponse.url, file as File, () => {});
    const blobDetails = await getBlob({ resourceIds: createBlobResponse.blobId }, dispatch);
    const blob = blobDetails[0];

    if (!blob) {
      throw new Error('Unable to upload file');
    }

    return {
      id: blob.blobId,
      fileName,
      url: blob.url,
      type: fileType,
    };
  };

  const handleBlur = (fieldName: string, e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (typeof value === 'string') {
      form.setFieldsValue({
        [fieldName]: value.trim(),
      });
    }
  };
  return (
    <Form
      form={form}
      className={classes.form}
      initialValues={{
        reminder: true,
        receiverTypes: [],
        channels: [],
        roles: [],
        ...initialValues,
      }}
      onFinish={onFinish}
    >
      <div className={classes.sectionTitle}>Basic Information</div>
      <div className={classes.fieldRow}>
        <Form.Item
          name="name"
          rules={[
            requiredRule('Please enter the Event Name'),
            { max: 255, message: 'Event name cannot exceed 255 characters' },
          ]}
          label="Event Name"
          required
          showColon
        >
          {({ value, onChange }) => {
            return (
              <TextField
                size="small"
                className={classes.eventName}
                fullWidth
                variant="outlined"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={(e) => handleBlur('name', e)}
              />
            );
          }}
        </Form.Item>
      </div>
      <div className={classes.fieldRow}>
        <div className={classes.dateRange}>
          <div className={classes.fieldRow} style={{ alignItems: 'center' }}>
            <span
              className={classes.sectionTitle}
              style={{
                minWidth: 148,
              }}
            >
              Start Date (T){' '}
              <span
                style={{
                  color: 'red',
                }}
              >
                *
              </span>
              {' :'}
            </span>
            <Form.Item
              name="startDate"
              dependencies={['status']}
              rules={dateRule('Please select the Event Start Date', (_rule, value) => {
                if (value && value < new Date()) {
                  return Promise.reject(new Error('Start Date must be greater than or equal to Current Date'));
                }
                return Promise.resolve();
              })}
              style={{
                width: 260,
              }}
            >
              {({ value, onChange }, _meta, currentForm) => {
                const isEventDateDisabled = currentForm.getFieldValue('status') === 'Published';
                return (
                  <PruDateTimePicker
                    slotProps={{ textField: { size: 'small', placeholder: 'Please select' } }}
                    className={classes.datePicker}
                    value={value}
                    onChange={onChange}
                    format="DD/MM/YYYY HH:mm"
                    disabled={isEventDateDisabled}
                  />
                );
              }}
            </Form.Item>
          </div>

          <span className={classes.dateSeparator}>-</span>
          <div className={classes.fieldRow} style={{ alignItems: 'center' }}>
            <span
              className={classes.sectionTitle}
              style={{
                minWidth: 100,
              }}
            >
              End Date (T){' '}
              <span
                style={{
                  color: 'red',
                }}
              >
                *
              </span>
              {' :'}
            </span>
            <Form.Item
              name="endDate"
              dependencies={['startDate', 'status']}
              rules={dateRule('Please select the Event End Date', (_rule, value) => {
                const startDate = form.getFieldValue('startDate') as Date | null;
                if (value && startDate && value <= startDate) {
                  return Promise.reject(new Error('Event End Date must be later than to Event Start Date'));
                }
                return Promise.resolve();
              })}
              style={{
                width: 260,
              }}
            >
              {({ value, onChange }, _meta, currentForm) => {
                const isEventDateDisabled = currentForm.getFieldValue('status') === 'Published';
                return (
                  <PruDateTimePicker
                    slotProps={{ textField: { size: 'small', placeholder: 'Please select' } }}
                    className={classes.datePicker}
                    value={value}
                    onChange={onChange}
                    format="DD/MM/YYYY HH:mm"
                    disabled={isEventDateDisabled}
                  />
                );
              }}
            </Form.Item>
          </div>
        </div>
      </div>
      <div
        className={classes.fieldRow}
        style={{
          gap: 0,
        }}
      >
        <div
          style={{
            width: 160,
            flexShrink: 0,
          }}
        ></div>
        <Form.Item
          name="reminder"
          valuePropName="checked"
          style={{
            flex: 1,
            minWidth: 0,
          }}
        >
          {({ checked, onChange }) => (
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={checked ?? false}
                  onChange={(event) => onChange(event.target.checked)}
                />
              }
              label="Remind Me"
            />
          )}
        </Form.Item>
      </div>

      <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.reminder !== currentValues.reminder}>
        {(_, _meta, currentForm) => {
          const isReminder = currentForm.getFieldValue('reminder');

          return isReminder ? (
            <>
              <Form.Item
                name="daysBeforeEvent"
                label="Reminder Event Date (X)"
                showColon
                rules={[
                  requiredRule('Reminder Event Date must be greater than 0'),
                  {
                    validator: (_rule: unknown, value: number | string) => {
                      if (value !== undefined && value !== null && Number(value) <= 0) {
                        return Promise.reject(new Error('Reminder Event Date must be greater than 0'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                required
              >
                {({ value, onChange }) => (
                  <div className={classes.fieldRow}>
                    <span className={classes.sectionTitle}>Before</span>
                    <TextField
                      size="small"
                      type="number"
                      className={classes.reminderDays}
                      inputProps={{ min: 1 }}
                      variant="outlined"
                      value={value ?? ''}
                      onChange={(event) => onChange(event.target.value)}
                    />
                    <span>Days</span>
                  </div>
                )}
              </Form.Item>

              <div className={classes.notificationSection}>
                <span className={classes.sectionTitle}>
                  Reminder Notification (T-X){' '}
                  <span
                    style={{
                      color: 'red',
                    }}
                  >
                    *
                  </span>
                </span>
                <Form.Item
                  name={['reminderNotification', 'titleMsg']}
                  label="Title"
                  rules={notificationTitleRules('Please enter a notification message')}
                  required
                  showColon
                >
                  {({ value, onChange }) => (
                    <TextField
                      id="reminder-title"
                      size="small"
                      className={classes.fullWidth}
                      variant="outlined"
                      value={value ?? ''}
                      onChange={(event) => onChange(event.target.value)}
                      onBlur={(e) => handleBlur('reminderNotification.titleMsg', e)}
                    />
                  )}
                </Form.Item>
                <Form.Item
                  name={['reminderNotification', 'contentMsg']}
                  label="Message"
                  rules={notificationMessageRules}
                  required
                  showColon
                >
                  {({ value, onChange }) => (
                    <TextField
                      id="reminder-message"
                      size="small"
                      multiline
                      minRows={2}
                      className={classes.fullWidth}
                      variant="outlined"
                      value={value ?? ''}
                      onChange={(event) => onChange(event.target.value)}
                      onBlur={(e) => handleBlur('reminderNotification.contentMsg', e)}
                    />
                  )}
                </Form.Item>
              </div>
            </>
          ) : null;
        }}
      </Form.Item>
      <div className={classes.notificationSection}>
        <span className={classes.sectionTitle}>
          Event Day Notification (T){' '}
          <span
            style={{
              color: 'red',
            }}
          >
            *
          </span>
        </span>
        <Form.Item
          name={['eventNotification', 'titleMsg']}
          label="Title"
          rules={notificationTitleRules('Please enter a notification message')}
          required
          showColon
        >
          {({ value, onChange }) => (
            <TextField
              id="event-title"
              size="small"
              className={classes.fullWidth}
              variant="outlined"
              value={value ?? ''}
              onChange={(event) => onChange(event.target.value)}
              onBlur={(e) => handleBlur('eventNotification.titleMsg', e)}
            />
          )}
        </Form.Item>
        <Form.Item
          name={['eventNotification', 'contentMsg']}
          label="Message"
          rules={notificationMessageRules}
          required
          showColon
        >
          {({ value, onChange }) => (
            <TextField
              id="event-message"
              size="small"
              multiline
              minRows={2}
              className={classes.fullWidth}
              variant="outlined"
              value={value ?? ''}
              onChange={(event) => onChange(event.target.value)}
              onBlur={(e) => handleBlur('eventNotification.contentMsg', e)}
            />
          )}
        </Form.Item>
      </div>
      <div className={classes.fieldColumn}>
        <div className={classes.sectionTitle}>Target Receiver</div>
        <Form.Item
          name="receiverTypes"
          label="Type of view"
          required
          dependencies={['channels', 'roles', 'userFile']}
          rules={[
            {
              validator: (_rule, value: string[] = []) => {
                const channels = form.getFieldValue('channels') as string[] | undefined;
                const roles = form.getFieldValue('roles') as string[] | undefined;
                const hasChannel = Boolean(channels?.length);
                const hasRoles = Boolean(roles?.length);
                const hasGroupOfUsers = value.includes(ETypeOfView.GroupOfUsers);

                if (!hasChannel && !hasRoles && !hasGroupOfUsers) {
                  return Promise.reject(new Error('Empty value is not allowed'));
                }

                if ((hasChannel && hasRoles && !hasGroupOfUsers) || (!hasChannel && !hasRoles && hasGroupOfUsers)) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error(receiverValidationMessage));
              },
            },
          ]}
          labelTop
        >
          {({ value = [], onChange }) => {
            const validateReceiverSelection = () => {
              void form.validateFields(['receiverTypes', 'userFile']).catch(() => undefined);
            };

            const updateReceiverTypes = (nextTypes: string[]) => {
              onChange(Array.from(new Set(nextTypes)));
              validateReceiverSelection();
            };

            const setReceiverType = (receiverType: (typeof receiverTypeOptions)[number], checked: boolean) => {
              if (receiverType === ETypeOfView.GroupOfUsers && checked) {
                updateReceiverTypes([...value, ETypeOfView.GroupOfUsers]);
                return;
              }

              const nextTypes = checked
                ? [...value, receiverType]
                : value.filter((item: string) => item !== receiverType);
              updateReceiverTypes(nextTypes);

              if (receiverType === 'Channel') {
                form.setFieldsValue({ channels: checked ? channelOptions.map((option) => option.value) : [] });
              }
              if (receiverType === 'Roles') {
                form.setFieldsValue({ roles: checked ? roleOptions.map((option) => option.value) : [] });
              }
            };

            return (
              <div className={classes.fieldColumn}>
                {receiverTypeOptions.map((receiverType) => (
                  <div key={receiverType} className={classes.fieldRow}>
                    <FormControlLabel
                      className={classes.receiverType}
                      style={{
                        marginRight: 0,
                        minWidth: 100,
                      }}
                      control={
                        <Checkbox
                          size="small"
                          checked={value.includes(receiverType)}
                          onChange={(event) => setReceiverType(receiverType, event.target.checked)}
                        />
                      }
                      label={receiverType !== ETypeOfView.GroupOfUsers ? `${receiverType} :` : 'Group of users'}
                    />
                    {receiverType === 'Channel' && (
                      <Form.Item noStyle name="channels">
                        {({ value: selectedChannels = [], onChange: onChannelsChange }) => (
                          <div className={classes.receiverOptions}>
                            {channelOptions.map((channel) => (
                              <FormControlLabel
                                key={channel.value}
                                className={classes.checkbox}
                                style={{
                                  marginRight: 0,
                                  minWidth: 60,
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedChannels.includes(channel.value)}
                                    onChange={(event) => {
                                      const nextChannels = event.target.checked
                                        ? [...selectedChannels, channel.value]
                                        : selectedChannels.filter((item: string) => item !== channel.value);
                                      onChannelsChange(nextChannels);
                                      form.setFieldsValue({
                                        receiverTypes: nextChannels.length
                                          ? Array.from(new Set([...value, 'Channel']))
                                          : value.filter((item: string) => item !== 'Channel'),
                                      });
                                      validateReceiverSelection();
                                    }}
                                  />
                                }
                                label={channel.label}
                              />
                            ))}
                          </div>
                        )}
                      </Form.Item>
                    )}
                    {receiverType === 'Roles' && (
                      <Form.Item noStyle name="roles">
                        {({ value: selectedRoles = [], onChange: onRolesChange }) => (
                          <div className={classes.receiverOptions}>
                            {roleOptions.map((role) => (
                              <FormControlLabel
                                key={role.value}
                                className={classes.checkbox}
                                style={{
                                  marginRight: 0,
                                  minWidth: 60,
                                }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={selectedRoles.includes(role.value)}
                                    onChange={(event) => {
                                      const nextRoles = event.target.checked
                                        ? [...selectedRoles, role.value]
                                        : selectedRoles.filter((item: string) => item !== role.value);
                                      onRolesChange(nextRoles);
                                      form.setFieldsValue({
                                        receiverTypes: nextRoles.length
                                          ? Array.from(new Set([...value, 'Roles']))
                                          : value.filter((item: string) => item !== 'Roles'),
                                      });
                                      validateReceiverSelection();
                                    }}
                                  />
                                }
                                label={role.label}
                              />
                            ))}
                          </div>
                        )}
                      </Form.Item>
                    )}
                  </div>
                ))}
                {value.includes(ETypeOfView.GroupOfUsers) && (
                  <>
                    <Form.Item
                      name="userFile"
                      dependencies={['receiverTypes', 'channels', 'roles']}
                      rules={[
                        {
                          validator: (_rule, file) => {
                            const channels = form.getFieldValue('channels') as string[] | undefined;
                            const roles = form.getFieldValue('roles') as string[] | undefined;
                            const hasInvalidReceiverCombination = Boolean(channels?.length || roles?.length);

                            if (hasInvalidReceiverCombination) {
                              return Promise.resolve();
                            }

                            return !file ? Promise.reject(new Error('Upload file cannot be empty')) : Promise.resolve();
                          },
                        },
                      ]}
                      style={{ marginLeft: 20 }}
                    >
                      {({ value: userFile, onChange: onFileChange }) => (
                        <div className={classes.uploadRow}>
                          <div style={{ paddingLeft: 4.2, paddingRight: 15 }}>
                            <SubdirectoryArrowRight></SubdirectoryArrowRight>
                          </div>
                          <FileUploader
                            upload={uploadGroupFile}
                            value={userFile}
                            color="inherit"
                            onChange={onFileChange}
                            showAllowText="Upload the excel file list of agent (max = 10 MB)"
                            fileTypeErrorText="Please upload one of these formats: CSV, excel"
                            fileSizeErrorText="Cannot have more than 10MB"
                            maxFileSize={10}
                            allowedFileTypes={spreadsheetFileTypes}
                          />
                        </div>
                      )}
                    </Form.Item>
                    <div className={classes.templateLink}>
                      <a href={toAbsoluteUrl('/templates/Template_Group_Of_User.csv')}>Download template</a>
                    </div>
                  </>
                )}
              </div>
            );
          }}
        </Form.Item>
      </div>
    </Form>
  );
};
